import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow, format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  FileSignature,
  Download,
  LogIn,
  LogOut,
  Settings,
  Eye,
} from 'lucide-react';
import type { AuditLogRecord, ActionCategory } from '@/types/compliance';
import {
  actionCategoryLabels,
  sensitivityLabels,
  sensitivityColors,
} from '@/types/compliance';

const actionIcons: Record<ActionCategory, React.ElementType> = {
  create: Plus,
  read: Eye,
  update: Pencil,
  delete: Trash2,
  approve: Check,
  reject: X,
  sign: FileSignature,
  export: Download,
  login: LogIn,
  logout: LogOut,
  config: Settings,
};

const actionColors: Record<ActionCategory, string> = {
  create: 'bg-green-500',
  read: 'bg-gray-400',
  update: 'bg-blue-500',
  delete: 'bg-red-500',
  approve: 'bg-emerald-500',
  reject: 'bg-orange-500',
  sign: 'bg-purple-500',
  export: 'bg-cyan-500',
  login: 'bg-indigo-500',
  logout: 'bg-slate-500',
  config: 'bg-amber-500',
};

interface AuditTimelineProps {
  records: AuditLogRecord[];
  onSelect?: (record: AuditLogRecord) => void;
  maxItems?: number;
}

export function AuditTimeline({ records, onSelect, maxItems = 10 }: AuditTimelineProps) {
  const displayRecords = records.slice(0, maxItems);

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/50 via-primary/30 to-transparent" />

      <div className="space-y-1">
        {displayRecords.map((record, index) => {
          const Icon = actionIcons[record.action_category] || Pencil;
          const isFirst = index === 0;
          const isLast = index === displayRecords.length - 1;

          return (
            <div
              key={record.id}
              className={cn(
                "relative flex gap-4 p-3 rounded-lg transition-all cursor-pointer group",
                "hover:bg-muted/50",
                isFirst && "bg-muted/30"
              )}
              onClick={() => onSelect?.(record)}
            >
              {/* Timeline dot */}
              <div className="relative z-10 flex-shrink-0">
                <div
                  className={cn(
                    "w-12 h-12 rounded-full flex items-center justify-center shadow-sm",
                    "transition-transform group-hover:scale-110",
                    actionColors[record.action_category]
                  )}
                >
                  <Icon className="w-5 h-5 text-white" />
                </div>
                {isFirst && (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-full animate-pulse" />
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">
                        {record.entity_type}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {actionCategoryLabels[record.action_category]}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={cn("text-xs", sensitivityColors[record.sensitivity_level])}
                      >
                        {sensitivityLabels[record.sensitivity_level]}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5 truncate">
                      {record.change_summary || record.action}
                    </p>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-medium">
                      {record.performed_by_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(record.timestamp_utc), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </p>
                  </div>
                </div>

                {/* Hash preview on hover */}
                <div className="overflow-hidden max-h-0 group-hover:max-h-8 transition-all duration-300">
                  <p className="text-xs font-mono text-muted-foreground mt-2 truncate">
                    Hash: {record.record_hash}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Show more indicator */}
        {records.length > maxItems && (
          <div className="relative flex items-center gap-4 pl-3 pt-2">
            <div className="w-12 flex justify-center">
              <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                <span className="text-xs text-muted-foreground">+{records.length - maxItems}</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {records.length - maxItems} registros más
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
